import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import cardValidator from 'card-validator';
import { emptyObject } from 'ui-components/default-props/empty';
import { noop } from 'ui-components/default-props/noop';

import Global from '../../../store/global-selectors';
import CreditCard from '../../../components/credit-card';
import CreditCardSchema from '../../../components/credit-card/schema';
import { useYupFormValidation } from '../../../validation/use-yup-form-validation';
import { AUD } from '../../../../src/constants/currency-codes';
import { CREDIT_CARD_PROVIDERS } from '../../../components/credit-card/constants';
import { US } from '../../../constants/country-codes';

import { messages } from '../../messages';
import style from './style.module.css';

const creditCardProviderTypeMap = {
  'american-express': CREDIT_CARD_PROVIDERS.AMERICAN_EXPRESS,
  visa: CREDIT_CARD_PROVIDERS.VISA,
  mastercard: CREDIT_CARD_PROVIDERS.MASTERCARD,
  discover: CREDIT_CARD_PROVIDERS.DISCOVER,
};

export const WalletCreditCardInfo = ({
  errors = emptyObject,
  onChange: onChangeProp = noop,
  onValidationChange: onValidationChangeProp = noop,
  paymentInfo,
  disabled = false,
  currency = '',
  showAllErrors = false,
}) => {
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);
  const { errors: validationErrors, isValid } = useYupFormValidation({
    form: paymentInfo,
    schema: CreditCardSchema,
  });
  useEffect(() => {
    onValidationChangeProp?.(isValid);
  }, [onValidationChangeProp, isValid]);

  const onChange = useCallback(
    (propertyName, value) => {
      onChangeProp({ propertyName, value });
    },
    [onChangeProp],
  );
  const creditCardErrors = useMemo(
    () => ({
      ...validationErrors,
      ...errors,
    }),
    [validationErrors, errors],
  );

  const getCCProvider = useCallback(() => {
    const cc = paymentInfo?.cardNumber;
    if (cc) {
      const numberValidation = cardValidator.number(`${cc}`);
      if (numberValidation.card) {
        const typeFromCardValidator = numberValidation.card.type;
        return creditCardProviderTypeMap[typeFromCardValidator] || CREDIT_CARD_PROVIDERS.OTHER;
      }
      return '';
    }
  }, [paymentInfo?.cardNumber]);
  useEffect(() => {
    const validCC = !Object.keys(validationErrors).includes('cardNumber');
    if (validCC) {
      const provider = getCCProvider();
      onChange('cardProvider', provider);
    }
  }, [getCCProvider, onChange, validationErrors]);

  const messageToShow = useCallback(() => {
    if (sellerHomeCountry === US) {
      return <FormattedMessage {...messages.creditCardPaymentMessage} />;
    }
    if (currency === AUD) {
      return <FormattedMessage {...messages.creditCardPaymentMessageAuOnly} />;
    }
    return null;
  }, [currency, sellerHomeCountry]);

  return (
    <div className={style['credit-card']}>
      <CreditCard
        creditCardInfo={paymentInfo}
        disabled={disabled}
        errors={creditCardErrors}
        onChange={onChange}
        showAllErrors={showAllErrors}
      />
      <div className={style['credit-card-payment-message']}>{messageToShow()}</div>
    </div>
  );
};

WalletCreditCardInfo.propTypes = {
  errors: PropTypes.object,
  paymentInfo: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  onValidationChange: PropTypes.func,
  disabled: PropTypes.bool,
  currency: PropTypes.string,
  showAllErrors: PropTypes.bool,
};
